/* eslint-disable no-unused-vars */
import API from 'shared/constants/api';
import snakeCaseKeys from 'snakecase-keys';
import { replaceSpace, subjectiveText } from 'shared/modules';
import queryString, { stringify } from 'query-string';
import isEmpty from 'lodash/isEmpty';
import {
  selectedActions,
  resetAll,
  resultsThunks,
  carFormFillThunks,
  carSetThunks,
  commercialFormFillThunks,
  motoFormFillThunks,
  setFormVocabulary,
  transportFillThunks,
  setData,
} from 'shared/actions/searchForm';
import { setSavedSearches } from 'shared/actions/user';
import COUNT_URL_PARAMS from 'shared/constants/json/seoText/countUrlParams.json';
import { getSeoParamsTitles } from 'shared/helpers/arrays';
import { setLocation, setSeoSearchUrl, seoRequest, directSetSeoText } from 'shared/actions/globals';
import { getAdsContext, getPriceValuation } from 'shared/helpers/adsAndMetrics';
import { getResultFromStoreByKey } from 'shared/helpers/common';
import DESCRIPTION from 'shared/constants/seoDescriptions';
import TITLE from 'shared/constants/seoTitles';
import META_HEADER from 'shared/constants/seoHeaders';
import types from 'shared/constants/json/transportTypes.json';
import COUNTRIES from 'shared/constants/json/countries.json';
import CITIES from 'shared/constants/json/cities.json';
import priceStatuses from 'shared/constants/json/priceStatuses.json';
import { removeLocaleFromHref, getAllLocations } from 'shared/helpers/locale';
import { getRootTransportType } from 'shared/helpers/transportTypes';
import { fetchVocabulary, getSearchQuery } from 'shared/helpers/search';
import { shouldHideSeoText, getSeoTextQuery } from 'shared/helpers/seoText';
import { translateSeoLinks } from 'shared/helpers/urls';

import { bodyCategories } from './categoriesSearch';
import history from '../../../history';

const ALL_LOCATIONS = getAllLocations();
const SPECIAL_TECH_ID = 23;
const isMobile = __APP__ === 'mobile';
const isClient = process.env.BROWSER;

/**
 * Get links for links blocks.
 * @param {Function} fetch
 * @param {Object} q
 * @returns {Promise<Object>}
 */
const getCanonicalListPromise = (fetch, q) => {
  if (q.transport && q.transport === SPECIAL_TECH_ID && !q.transportChild) {
    return Promise.resolve(undefined);
  }

  if (!q.make) {
    return fetch(`${API.ADVERT.MAKES_COUNT}?${stringify(snakeCaseKeys(q))}`).then(r => r.json());
  }
  if (q.make && !q.model) {
    return fetch(`${API.ADVERT.MODELS_COUNT}?${stringify(snakeCaseKeys(q))}`).then(r => r.json());
  }

  return Promise.resolve(undefined);
};

async function getCategories(store, fetch, transportType) {
  const { searchForm: { vocabulary } } = store.getState();
  if (vocabulary && vocabulary.categories) {
    return vocabulary.categories;
  }
  const categories = await fetch(API.VOCABULARY.categories(transportType.id)).then(r => r.json());
  store.dispatch(setFormVocabulary({ categories }));
  return categories;
}

async function getDrives(store, fetch, transportType) {
  const { searchForm: { vocabulary } } = store.getState();
  if (vocabulary && vocabulary.drives) {
    return vocabulary.drives;
  }
  const drives = await fetch(API.VOCABULARY.drives(transportType.id)).then(r => r.json());
  store.dispatch(setFormVocabulary({ drives }));
  return drives;
}

export default async function(data) {
  const { fetch, store, baseUrl, path, query, translate, url: currentUrl } = data;
  const url = removeLocaleFromHref(`${baseUrl}${path}`.replace('//', '/'));
  if (query.currency) {
    store.dispatch(transportFillThunks.currency(query.currency));
  }
  const seoResponse = await store.dispatch(seoRequest(url, query));

  if (isEmpty(seoResponse)) {
    return null;
  }

  const { seo: seoTags, kind, meta, ...seoQuery } = seoResponse;
  let seoAPITitle = '';
  let seoAPIDescription = '';
  let seoAPIH1 = '';

  const characteristics = translateSeoLinks(seoResponse.characteristics, translate);

  const { year } = seoQuery;
  if (history && !year && query.yearFrom && query.yearFrom === query.yearTo) {
    history.push(`${currentUrl}god-${query.yearFrom}/?${queryString.stringify(query)}`);
  }

  const transportType = seoQuery.transport || types.find(item => item.slug === 'avto');

  const isNew = url.includes('novye') || query.isNew || null;
  const isAuto = transportType.slug === 'avto';
  const isUsedAuto = isAuto && !isNew;
  let description;
  let title;
  let metaHeader;

  if (!isUsedAuto && seoQuery.location && seoQuery.location.is_country) {
    return null;
  }

  const state = store.getState();
  const { searchForm: { vocabulary }, user: { token, savedSearches: storeSavedSearches } } = state;
  const prevDealer = state.searchForm.selected.dealer;
  const { location: storeLocation, locale } = state.globals;

  store.dispatch(selectedActions.set.dealer(null));

  // Set country to search form field.
  if (seoQuery.location && seoQuery.location.is_country) {
    store.dispatch(selectedActions.set.drivenFrom(seoQuery.location));
  }

  if (typeof seoTags === 'object') {
    const LangPrefix = locale === 'ru' ? '' : `${locale}_`;
    seoAPIDescription = seoTags[`${LangPrefix}description`] || '';
    seoAPITitle = seoTags[`${LangPrefix}title`] || '';
    const seoTagsData = seoTags[`${LangPrefix}data`] || null;
    seoAPIH1 = seoTagsData && (seoTagsData.H1 || seoTagsData.h1);
  }

  // start seoText logic
  const isUk = locale === 'uk';
  const seoTextQuery = getSeoTextQuery(baseUrl, path, isUk, COUNT_URL_PARAMS.avto);
  const hideSeoText = shouldHideSeoText(seoResponse, url, query);

  if (!hideSeoText) {
    await fetch(API.SEO.SEARCH_TEXT(seoTextQuery))
      .then(res => res.json())
      .then(response =>
        store.dispatch(directSetSeoText(response.err || hideSeoText ? null : response)),
      );
    // end seoText logic
  } else {
    store.dispatch(directSetSeoText(null));
    console.info('No seo text for: ', seoTextQuery);
  }

  // start get && set saved searches
  if (token && !storeSavedSearches) {
    const savedSearches = await fetch(API.ADVERT.SEARCH_SUB, {
      method: 'GET',
      headers: {
        Authorization: `JWT ${token}`,
      },
    })
      .then(res => res.json())
      .then(resp => (resp && resp.length ? resp : []));

    store.dispatch(setSavedSearches(savedSearches));
  }
  // end get && set saved searches
  // fetch listing news
  const news = isMobile
    ? await fetch(
        API.JOURNAL.LIST({
          tag: undefined,
          page: 1,
          perPage: 1,
          rubric: 'news',
          language: locale,
        }),
      )
        .then(r => r.json())
        .then(r => r.results)
        .then(resp => (resp && resp.length ? resp : []))
    : [];
  const prevSelectedTransport = state.searchForm.selected && state.searchForm.selected.transport;
  if ((prevSelectedTransport && prevSelectedTransport.id !== transportType.id) || prevDealer) {
    store.dispatch(resetAll());
  }
  store.dispatch(selectedActions.set.transport(transportType));

  if (storeLocation && storeLocation.is_country && !isUsedAuto) {
    store.dispatch(setLocation(null));
  }

  const rootTransportType = getRootTransportType(transportType);
  const isMotoSearch = rootTransportType && rootTransportType.slug === 'moto';
  const isCommercial =
    (rootTransportType && rootTransportType.slug === 'commercial') ||
    transportType.slug === 'avtobus' ||
    transportType.slug === 'pritsepy' ||
    transportType.slug === 'spetstehnika' ||
    transportType.slug === 'gruzovye' ||
    transportType.slug === 'vodnyi-transport';

  const brands = await fetch(`${API.ADVERT.MAKES_COUNT}?transport=${transportType.id}`).then(r =>
    r.json(),
  );

  // Fill data for search form on client side only
  if (isClient) {
    const neededVocabulary = await fetchVocabulary(vocabulary, transportType, fetch);
    store.dispatch(
      setFormVocabulary({
        ...neededVocabulary,
        priceStatuses,
      }),
    );
  }

  let body = seoQuery.category || seoQuery.transport_category || {};
  if (!body.title && query.category) {
    const categories = await getCategories(store, fetch, transportType);

    for (let a = 0; categories && a < categories.length; a += 1) {
      const buf = categories[a] && categories[a].title && categories[a].id;
      if (buf && String(buf) === query.category) {
        body = categories[a];
        break;
      }
    }
  }

  let drive;
  let seoDrive;
  if (query.drive) {
    const drives = await getDrives(store, fetch, transportType);
    for (let a = 0; drives && a < drives.length; a += 1) {
      const buf = drives[a] && drives[a].title && drives[a].id;
      if (buf && String(buf) === query.drive) {
        drive = drives[a].title;
        seoDrive = drives[a];
        break;
      }
    }
  }

  const { selected } = store.getState().searchForm;
  const { currency } = selected;
  const currentPage = query.page ? parseInt(query.page, 10) : 1;
  const q = { ...query };
  let location = state.globals.location;
  const lang = state.globals.locale;

  if (seoQuery.make) {
    q.make = seoQuery.make.id;
  }

  if (seoQuery.model) {
    q.model = seoQuery.model.id;
  }

  if (seoQuery.category) {
    q.category = seoQuery.category.id;
  }

  if (seoQuery.transport_category) {
    q.body = seoQuery.transport_category.title;
  }

  if (seoQuery.transport_category) {
    q.transportChild = seoQuery.transport_category.id;
  }

  if (seoQuery.year) {
    q.yearFrom = seoQuery.year;
    q.yearTo = seoQuery.year;
  }

  if (seoQuery.engine) {
    q.engine = seoQuery.engine.id;
  }

  if (seoQuery.gearbox) {
    q.gearbox = seoQuery.gearbox.id;
  }

  if (seoQuery.location) {
    location = seoQuery.location;
    store.dispatch(setLocation(seoQuery.location));
  }

  // eslint-disable-next-line no-prototype-builtins
  if (q.hasOwnProperty('from_dealer')) {
    q.fromDealer = +q.from_dealer;
    delete q.from_dealer;
  }

  q.location = (location && location.id) || undefined;
  q.transport = transportType.id;
  q.currency = currency && currency.slug;
  delete q.page;

  q.isNew = isNew === null ? null : +isNew;
  q.isCrashed = q.isNew ? null : q.isCrashed;
  seoQuery.isNew = q.isNew;

  const carsListTypes = {
    result: stringify(getSearchQuery({ ...q, page: currentPage })),
    vip: stringify(getSearchQuery({ ...q, perPage: 6, isVip: true })),
    top: stringify(getSearchQuery({ ...q, perPage: 5, isTop: true })),
  };

  const [canonicalList, carListResult] = await Promise.all([
    isMobile ? Promise.resolve([]) : getCanonicalListPromise(fetch, q),
    store.dispatch(resultsThunks.searchResults(carsListTypes)),
  ]);

  const carsResult = {
    result: carListResult[0].result,
    vip: carListResult[1].vip,
    top: carListResult[2].top,
  };

  const seoLocation = [...ALL_LOCATIONS, ...COUNTRIES].find(
    item => item.id === (location && location.id),
  );
  const seoLocationMoto = [...CITIES, ...COUNTRIES].find(
    item => item.id === (location && location.id),
  );

  const seoLocationTitle = seoLocationMoto ? seoLocationMoto.where[locale] : undefined;
  const country = seoLocation && seoLocation.is_country ? seoLocation.from[locale] : undefined;

  const metaParams = {
    type: transportType,
    make: seoQuery.make,
    model: seoQuery.model,
    modification: seoQuery.modification,
    category: seoQuery.category,
    gearbox: seoQuery.gearbox,
    engine: seoQuery.engine,
    location: seoLocationTitle || (location && `в ${location.title}`),
    yearTo: q.yearTo,
    yearFrom: q.yearFrom,
    isNew: q.isNew,
    country,
    year: seoQuery.year,
    body: q.body,
  };
  const { make = {}, model = {} } = seoQuery;

  if (q.generation && !seoQuery.generation) {
    seoQuery.generation = await fetch(API.SEO.SEARCH_GENERATIONS(q.generation)).then(
      r => (r.ok ? r.json() : {}),
    );
  }
  const seo = {
    ...seoQuery,
    location: seoLocation,
  };
  const defaultCategoryTitleHeader = model.title
    ? translate('авто')
    : translate('легковой автомобиль');
  const defaultCategoryDescriptions =
    model.title && !seoQuery.category ? translate('авто') : translate('легковой автомобиль');

  const transportCategoryTitle =
    (seoQuery.transport_category && seoQuery.transport_category.title) || '';

  const transportTypeTitle = (transportType && transportType.title) || '';
  const commonArguments = subjectiveText({
    make: (make && make.title) || '',
    makeTranslit: make[`${lang}_title`] || make.title || '',
    model: model.title || '',
    modelTranslit: model[`${lang}_title`] || model.title || '',
    transportTitle: (isAuto && transportCategoryTitle) || transportTypeTitle,
    transportNextTitle: transportTypeTitle,
    transportDescriptions: (isAuto && transportCategoryTitle) || transportTypeTitle,
    transportNextDescriptions: transportTypeTitle,
    transportHeader: transportCategoryTitle || transportTypeTitle,
    transportNextHeader: transportCategoryTitle || transportTypeTitle,
    categoryHeader: !model.title && body.title ? body.title : defaultCategoryTitleHeader,
    categoryTitle: body.title || defaultCategoryTitleHeader,
    categoryDescriptions: body.title || defaultCategoryDescriptions,
    reductionTranslit: transportType.slug === 'avtobus' ? 'бус' : '',
    cityTitle: (location && location.title) || 'Украина',
    cityDescriptions: (location && location.title) || 'Украина',
    cityHeader: (location && location.title) || 'Украина',
    transportCategoryTitle,
    generation:
      (query.generation && seoQuery.generation && seoQuery.generation.title) ||
      (query.generation && ' ') ||
      '',
    driveUnit: drive || '',
    engine: (seo.engine && seo.engine.title) || '',
    transmission: (metaParams.gearbox && metaParams.gearbox.title) || '',
    body: body.title || '',
    subsection: (seoQuery && seoQuery.part_type && seoQuery.part_type.title) || '',
    isNew: q.isNew,
    transportType: (transportType && transportType.slug) || '',
    allData: {
      url,
      baseUrl,
      locale,
      translate,
    },
    yearTitle: year ? `${year} ${translate('года')} ` : '',
    yearDescriptions: year ? `${year} ${translate('г.')} ` : '',
    motoHeader: transportCategoryTitle,
    motoTitle: transportCategoryTitle,
  });

  commonArguments.transportCategoryTitleOrigin =
    (seoQuery.transport_category && seoQuery.transport_category.title) || '';

  commonArguments.transportTitleOrigin = (seoQuery.transport && seoQuery.transport.title) || '';

  if (rootTransportType.slug === 'moto') {
    const type = rootTransportType.slug;
    const allMetaParams = { ...commonArguments, ...metaParams };

    // description = seoAPIDescription || DESCRIPTION.SEARCH[key](allMetaParams, translate);
    // title = seoAPITitle || TITLE.SEARCH[key](allMetaParams, translate);
    // metaHeader =
    //   seoAPIH1 ||
    //   META_HEADER.SEARCH[key](
    // {
    //   ...allMetaParams,
    //   page: currentPage,
    //   transport: transportType,
    //   locationObj: seoLocation || location,
    // },
    //     translate,
    //   );

    title =
      seoAPITitle ||
      TITLE.SEARCH[type](
        {
          ...allMetaParams,
          make: make && make.title ? make.title : '',
          model: model && model.title ? model.title : '',
          location: location && location.title ? location.title : '',
        },
        translate,
      );
    description =
      seoAPIDescription ||
      DESCRIPTION.SEARCH[type](
        {
          ...allMetaParams,
          make: make && make.title ? make.title : '',
          model: model && model.title ? model.title : '',
          location: location && location.title ? location.title : '',
        },
        translate,
      );
    metaHeader =
      seoAPIH1 ||
      META_HEADER.SEARCH[type](
        {
          ...allMetaParams,
          make: make && make.title ? make.title : '',
          model: model && model.title ? model.title : '',
          location: location && location.title ? location.title : '',
        },
        translate,
      );
  } else if (rootTransportType.slug === 'avto') {
    const navtoSeoParams = getSeoParamsTitles(
      { ...seo, body, drive: seoDrive, country: seoLocation, location },
      translate,
    );
    title = seoAPITitle || replaceSpace(TITLE.SEARCH.avto(navtoSeoParams, translate));
    description =
      seoAPIDescription || replaceSpace(DESCRIPTION.SEARCH.avto(navtoSeoParams, translate));
    metaHeader = seoAPIH1 || replaceSpace(META_HEADER.SEARCH.avto(navtoSeoParams, translate));
  } else {
    title = seoAPITitle || replaceSpace(TITLE.SEARCH.commercial(commonArguments, translate));
    description =
      seoAPIDescription || replaceSpace(DESCRIPTION.SEARCH.commercial(commonArguments, translate));
    metaHeader =
      seoAPIH1 || replaceSpace(META_HEADER.SEARCH.commercial(commonArguments, translate));
  }

  const result = getResultFromStoreByKey(store, carsListTypes.result);
  const isLastPage = !(result && result.next);
  store.dispatch(setSeoSearchUrl({ ...seoResponse, url }));
  store.dispatch(selectedActions.set.isNew(q.isNew));

  let formFillThunks = carFormFillThunks;
  if (isMotoSearch) {
    formFillThunks = motoFormFillThunks;
  } else if (isCommercial) {
    formFillThunks = commercialFormFillThunks;
  }

  if (q.make && !q.prepaid) {
    store.dispatch(setData.brandToChose(make));
  }

  Object.keys(q).forEach(key => {
    const item = q[key];
    if (formFillThunks[key]) {
      store.dispatch(formFillThunks[key](item));
    }
  });

  await store.dispatch(carSetThunks.count(q));
  //
  const {
    searchForm: { data: { count = 0 } = {}, results: { result: { results = [] } = {} } = {} },
  } = store.getState();
  if (!body.slug && seoQuery.make && seoQuery.model) {
    const categories = await getCategories(store, fetch, transportType);
    body.slug = bodyCategories(results, categories);
  }
  const targetingArguments = getAdsContext({
    make: seo.make,
    model: seo.model,
    isMain: seo.isMain,
    isNew: seo.isNew,
    rubric: seo.rubric,
    isCustomCleared: seo.isCustomCleared,
    location: seo.location,
    isCountry: seo.isCountry,
    transport: transportType,
    category: body,
    engine: seo.engine,
    dealer: seo.rubric,
    price: getPriceValuation({ from: q.priceFrom, to: q.priceTo }, q.currency),
    page: baseUrl.replace(/\//g, ''),
    id: seo.slug,
  });

  return {
    news,
    brands,
    hideSeoText,
    url,
    seoQuery,
    transportType,
    count,
    q,
    currentPage,
    carsListTypes,
    currency,
    title,
    description,
    metaHeader,
    targetingArguments,
    isLastPage,
    isMotoSearch,
    isCommercial,
    canonicalList,
    location,
    characteristics,
    shouldSetNoIndex: false, // Boolean(result && result.results && !result.results.length),
    shouldSetIndex: true, // Boolean(result && result.results && !result.results.length),
    carsResult,
  };
}



// WEBPACK FOOTER //
// ./src/shared/routes/search/index.js